import { AppShell, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ErrorBoundary } from '@sentry/react';
import { Outlet, useLocation } from 'react-router-dom';

import { AgentBubble } from 'src/Components/Agent/AgentBubble';
import { AnnouncementBanner } from 'src/Components/AnnouncementBanner';
import { AssetAnnouncementBanner } from 'src/Components/AssetAnnouncementBanner';
import { InstallBanner } from 'src/Components/InstallBanner';
import Menu from 'src/Components/Menu';
import NavBar from 'src/Components/NavBar';
import { ArchivedAssetWarning } from 'src/Containers/MainLayout/ArchivedAssetWarning';
import { PageErrorFallback } from 'src/Containers/MainLayout/PageErrorFallback';
import { PendingPlaceholderMatchPopup } from 'src/Containers/MainLayout/PendingPlaceholderMatchPopup';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { useFirstLoginDemoPopup } from 'src/Hooks/Shared/useFirstLoginDemoPopup';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { useMixpanelIdentify } from 'src/Hooks/Tracking/useMixpanelIdentify';
import { AssetDetailsDtoStatusEnum } from 'src/Services/API/generated';
import { trackErrorEncountered } from 'src/Services/Mixpanel/events';

import AssetAboveQuotaAlert from './AssetAboveQuotaAlert/AssetAboveQuotaAlert.component';
import styles from './MainLayout.module.scss';

const MainLayout = () => {
  const [opened, { toggle, close }] = useDisclosure();
  useFirstLoginDemoPopup();

  const { data: me } = useFetchUser();
  useMixpanelIdentify(me);
  const location = useLocation();

  const { data: asset } = useFetchAssetDetails();
  const assetAboveQuotas = useIsAssetAboveQuotas();

  return (
    <div className={styles.container}>
      <AppShell
        navbar={{
          width: 300,
          breakpoint: 'sm',
          collapsed: { mobile: !opened },
        }}
      >
        <AppShell.Navbar>
          <Menu toggleMenu={toggle} menuOpen={opened} closeMenu={close} />
        </AppShell.Navbar>
        <AppShell.Main>
          <NavBar toggleMenu={toggle} menuOpen={opened} />
          <InstallBanner />
          <AnnouncementBanner />
          <AssetAnnouncementBanner />
          {assetAboveQuotas && asset?.status === AssetDetailsDtoStatusEnum.Active && (
            <AssetAboveQuotaAlert />
          )}
          <PendingPlaceholderMatchPopup />
          <div className={styles.contentContainer}>
            {asset?.status === AssetDetailsDtoStatusEnum.Active ? (
              <ErrorBoundary
                key={location.pathname}
                fallback={({ resetError }) => <PageErrorFallback resetError={resetError} />}
                onError={error => {
                  console.error(error);
                  trackErrorEncountered({
                    error_message: error instanceof Error ? error.message : String(error),
                    page_context: window.location.pathname,
                  });
                }}
              >
                <Outlet />
              </ErrorBoundary>
            ) : asset?.status === AssetDetailsDtoStatusEnum.Archived ? (
              <ArchivedAssetWarning />
            ) : (
              <Loader />
            )}
          </div>
          {asset?.status === AssetDetailsDtoStatusEnum.Active && <AgentBubble />}
        </AppShell.Main>
      </AppShell>
    </div>
  );
};

export default MainLayout;
